import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import DataManagerLogo from '../resources/DataManagerLogo';
import './Auth.css';
import { useNavigate, useLocation } from 'react-router-dom';  // 👈 Importa hooks

/**
 * Auth - Premium Entry point for DataManager Authentication
 * Features Glassmorphism, Framer Motion animations, and Mesh Gradients.
 */
const Auth = ({ ipserv }) => {

    const navigate = useNavigate();  
    const location = useLocation();
    const [view, setView] = useState('login'); // 'login', 'register', 'forgot', 'reset', or 'success'
    const [resetToken, setResetToken] = useState(null);

    // Auto-redirect if already logged in or detect reset token
    React.useEffect(() => {
        if (localStorage.getItem('dm_admin')) {
            try {
                const profile = JSON.parse(localStorage.getItem('dm_admin') || '{}');
                if (profile.AdminRole === 'SUPERADMIN') {
                    navigate('/Core/SuperAdmin', { replace: true });
                } else {
                    navigate('/Core/Admin', { replace: true });
                }
            } catch {
                navigate('/Core/Admin', { replace: true });
            }
            return;
        }

        // Check for reset token in URL
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const isResetPath = location.pathname.includes('reset-password');
        
        if (token && isResetPath) {
            setResetToken(token);
            setView('reset');
        }
    }, [location, navigate]);

    const toggleView = () => {
        setView(view === 'login' ? 'register' : 'login');
    };

    const handleRegisterSuccess = () => {
        setView('success');
    };

    return (
        <div className="auth-page-container">
            {/* Background Animated Elements */}
            <div className="auth-blobs">
                <motion.div
                    className="auth-blob"
                    style={{ width: 400, height: 400, background: '#4f46e5', left: '10%', top: '20%' }}
                    animate={{
                        scale: [1, 1.2, 1],
                        x: [0, 50, 0],
                        y: [0, 30, 0]
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="auth-blob"
                    style={{ width: 300, height: 300, background: '#0ea5e9', right: '15%', bottom: '20%' }}
                    animate={{
                        scale: [1, 1.3, 1],
                        x: [0, -40, 0],
                        y: [0, 60, 0]
                    }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                />
            </div>

            {/* Content Split */}
            <div className="auth-side-form">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={view}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                        className="auth-card"
                    >
                        {view === 'login' ? (
                            <Login 
                                ipserv={ipserv} 
                                onToggle={toggleView} 
                                onForgot={() => setView('forgot')} 
                            />
                        ) : view === 'register' ? (
                            <Register 
                                ipserv={ipserv} 
                                onToggle={toggleView} 
                                onSuccess={handleRegisterSuccess} 
                                onForgot={() => setView('forgot')}
                            />
                        ) : view === 'forgot' ? (
                            <ForgotPassword 
                                ipserv={ipserv}
                                onBack={() => setView('login')} 
                                onSuccess={() => setView('forgot-sent')} 
                            />
                        ) : view === 'reset' ? (
                            <ResetPassword 
                                ipserv={ipserv}
                                token={resetToken} 
                                onSuccess={() => {
                                    setResetToken(null);
                                    setView('reset-success');
                                }} 
                            />
                        ) : view === 'forgot-sent' ? (
                            <div className="text-center p-3">
                                <div className="auth-state-icon mx-auto mb-4">
                                    <DataManagerLogo size={58} />
                                </div>
                                <h2 className="auth-title mb-3">Revisa tu correo</h2>
                                <p className="auth-subtitle mb-4">
                                    Si el email pertenece a una cuenta activa, enviamos un enlace seguro para restablecer la contraseña.
                                </p>
                                <div className="auth-note mb-4">
                                    El enlace vence en 60 minutos. Tambien revisa Spam o correo no deseado.
                                </div>
                                <button className="auth-btn-primary" onClick={() => setView('login')}>
                                    Volver al login
                                </button>
                            </div>
                        ) : view === 'reset-success' ? (
                            <div className="text-center p-3">
                                <div className="auth-state-icon mx-auto mb-4">
                                    <DataManagerLogo size={58} />
                                </div>
                                <h2 className="auth-title mb-3">Contraseña actualizada</h2>
                                <p className="auth-subtitle mb-4">
                                    Ya puedes ingresar con tus nuevas credenciales.
                                </p>
                                <button className="auth-btn-primary" onClick={() => setView('login')}>
                                    Iniciar sesion
                                </button>
                            </div>
                        ) : (
                            <div className="text-center p-4">
                                <div className="success-animation-icon mb-4 mx-auto">
                                    <div className="pulse-success"></div>
                                    <DataManagerLogo size={80} />
                                </div>
                                <h2 className="auth-title mb-3">¡Registro Exitoso!</h2>
                                <p className="mb-4 px-3" style={{ fontSize: '1.1rem', lineHeight: '1.6', color: 'var(--text-muted)' }}>
                                    Hemos enviado un enlace de verificación a tu correo electrónico.<br/>
                                    <strong>Por favor, confirma tu cuenta para poder ingresar.</strong>
                                </p>
                                <div className="p-3 bg-light border-radius-12 mb-5">
                                    <small className="text-muted">¿No recibiste el correo? Revisa tu carpeta de spam o espera unos minutos.</small>
                                </div>
                                <button 
                                    className="btn-premium-solid w-100 py-3"
                                    onClick={() => setView('login')}
                                >
                                    VOLVER AL INICIO DE SESIÓN
                                </button>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="auth-side-visual">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="text-center"
                >
                    <DataManagerLogo size={250} className="mb-4" />
                    <h2 className="auth-title mt-4" style={{ fontSize: '3.5rem' }}>DataManager</h2>
                    <p className="auth-subtitle" style={{ fontSize: '1.25rem', opacity: 0.7 }}>
                        Estructura Base para Gestión de Datos.<br />
                        Simplicidad. Poder. Elegancia.
                    </p>
                </motion.div>

                {/* Decorative Elements */}
                <div className="position-absolute bottom-0 start-0 p-5 opacity-25">
                    <small>© 2026 OctoCore Technologies</small>
                </div>
            </div>
        </div>
    );
};

export default Auth;
